import { CounterConfig, CounterState, CounterType } from '@gripp/shared-logic';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect } from 'react';
import { useController } from 'react-hook-form';
import { number, string, object as yupObject } from 'yup';
import { useFormContext } from '../../forms';
import { RoutineExecutionForm, RoutineExecutionFormProps } from '../types';
import { CounterExecutionComponent } from './counterExecutionComponent';

const roundDecimals = (value = 0, decimalPlaces: number): number => {
  const factor = 10 ** decimalPlaces;
  return Math.trunc(value * factor) / factor;
};

export const getExecutionFormDefaultValues = (
  _: CounterConfig,
  state: CounterState
) => {
  const previous = state?.state?.count;
  const baseline =
    _?.counterType === CounterType.Simple
      ? 0
      : roundDecimals(previous ?? 0, _?.decimalPlaces ?? 0);
  const counterType = _?.counterType ? _?.counterType : CounterType.Simple;
  return {
    count: previous ?? undefined,
    userCount: undefined,
    oldValue: baseline,
    counterType,
  } as any;
};

export const getExecutionFormResolver = () => {
  const validationSchema = yupObject({
    count: number()
      .transform((val, orig) => (orig === '' ? undefined : val))
      .nullable()
      .notRequired(),
    oldValue: number().notRequired().nullable(),
    userCount: number().notRequired().nullable(),
    counterType: string().notRequired(),
  });
  return yupResolver(validationSchema);
};

export const CounterExecutionForm: RoutineExecutionForm<
  CounterConfig,
  CounterState
> = (props: RoutineExecutionFormProps<CounterConfig, CounterState>) => {
  const formKey = 'count';
  const { control, setValue, getValues } = useFormContext();
  const { field } = useController({
    control: control,
    name: formKey,
  });

  useEffect(() => {
    const existing = props.currentState?.state?.count;
    if (existing !== undefined && existing !== null) {
      const baseline =
        props.config?.counterType === CounterType.Simple
          ? 0
          : roundDecimals(existing, props.config.decimalPlaces ?? 0);
      setValue('oldValue', baseline, {
        shouldDirty: false,
        shouldValidate: false,
      });
    } else {
      // Use the same baseline calculation as defaultValues to avoid marking form as dirty
      const baseline =
        props.config?.counterType === CounterType.Simple
          ? 0
          : roundDecimals(0, props.config.decimalPlaces ?? 0);
      setValue('oldValue', baseline, {
        shouldDirty: false,
        shouldValidate: false,
      });
    }
    if (field.value !== undefined && field.value !== null && field.value !== '')
      return;
    setValue(formKey, undefined as any, {
      shouldDirty: false,
      shouldValidate: false,
    });
    setValue('userCount', undefined as any, {
      shouldDirty: false,
      shouldValidate: false,
    });
  }, [props.currentState]);

  const handleChange = (value: number) => {
    if (value === null || Number.isNaN(value)) {
      setValue(formKey, undefined as any, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue('userCount', undefined as any, {
        shouldDirty: true,
        shouldValidate: true,
      });
      return;
    }
    const truncNumber = roundDecimals(value, props.config.decimalPlaces ?? 0);
    field.onChange(truncNumber);
    const existing = props.currentState?.state?.count;
    let baseline: number | undefined;
    if (existing !== undefined && existing !== null) {
      baseline =
        props.config?.counterType === CounterType.Simple
          ? 0
          : roundDecimals(existing, props.config.decimalPlaces ?? 0);
    } else {
      const oldVal = getValues('oldValue');
      baseline = oldVal !== undefined ? oldVal : undefined;
    }
    const delta =
      baseline === undefined
        ? truncNumber
        : roundDecimals(
            truncNumber - baseline,
            props.config.decimalPlaces ?? 0
          );
    setValue('userCount', delta, { shouldDirty: true, shouldValidate: true });
  };

  const initialValue =
    props.config?.counterType === CounterType.Simple
      ? 0
      : roundDecimals(
          props.currentState?.state.count || 0,
          props.config.decimalPlaces ?? 0
        );

  return (
    <CounterExecutionComponent
      onBlur={field.onBlur}
      instructions={props.config.instructions}
      initialValue={initialValue}
      onChange={handleChange}
      value={field.value}
      decimalPlaces={
        props.config.decimal ? props.config.decimalPlaces : undefined
      }
      counterType={props.config.counterType}
      isIssueEditRoutine={props.isIssueEditRoutine}
    />
  );
};
